import {
  BookedSkatingSessionHistoryResponse,
  CustomerWaiver,
  PaginatedResponse,
  RequestedByType,
  SkatingSession,
  TimeSlot,
} from '@types';
import {
  ConfirmationModal,
  Pagination,
  PDFViewerModal,
  SearchBar,
} from '_components/atoms';
import {
  AddTicketsToSessionDateTime,
  RemoveWaiverModal,
  SearchCustomerWaivers,
  ViewTicketAvailability,
} from '_components/molecules';
import { useAppSelector } from '_hooks';
import AdminService from '_services/AdminService';
import { ExcelExport } from '_utils/ExcelExport';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { FaEdit, FaFileSignature, FaHistory, FaReceipt } from 'react-icons/fa';
import { MdClose, MdFilterList } from 'react-icons/md';

function HistoryButton({
  hasHistory,
  onClick,
  size = 'sm',
}: {
  hasHistory?: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
}) {
  const recorded = !!hasHistory;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md transition-colors ${
        size === 'md' ? 'px-3 py-2 text-sm' : 'px-3 py-1 text-xs'
      } ${
        recorded
          ? 'bg-amber-100 font-semibold text-amber-900 ring-1 ring-amber-400 hover:bg-amber-200'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      title={
        recorded ? 'This ticket has recorded changes' : 'View change history'
      }
      aria-label={
        recorded
          ? 'View change history, changes recorded'
          : 'View change history'
      }
    >
      <FaHistory className={size === 'md' ? 'h-4 w-4' : 'h-3 w-3'} />
      History
      {recorded && (
        <span
          className="inline-block h-2 w-2 rounded-full bg-amber-500"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

const BookedSkatingSessions: React.FC = () => {
  const { accountDetails } = useAppSelector((state: any) => state.auth);
  const roleKey = accountDetails?.roleKey;
  const canCancel = ['admin', 'moderator'].includes(roleKey);
  const canDelete = roleKey === 'admin';

  const [sessions, setSessions] = useState<SkatingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState<string | null>(null);
  const [searchSessionId, setSearchSessionId] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [redeemingRecordId, setRedeemingRecordId] = useState<number | null>(
    null,
  );
  const [deletingRecordId, setDeletingRecordId] = useState<number | null>(null);
  const [permanentlyDeletingRecordId, setPermanentlyDeletingRecordId] =
    useState<number | null>(null);
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
    requestedByType: RequestedByType;
    reason: string;
  }>({
    isOpen: false,
    recordId: null,
    requestedByType: 'Staff',
    reason: '',
  });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
    requestedByType: RequestedByType;
    reason: string;
  }>({
    isOpen: false,
    recordId: null,
    requestedByType: 'Staff',
    reason: '',
  });
  const [renameModal, setRenameModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
    currentName: string;
    newCustomerName: string;
    requestedByType: RequestedByType;
    notes: string;
  }>({
    isOpen: false,
    recordId: null,
    currentName: '',
    newCustomerName: '',
    requestedByType: 'Staff',
    notes: '',
  });
  const [isRenaming, setIsRenaming] = useState(false);
  const [removeWaiverModal, setRemoveWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [isRemovingWaiver, setIsRemovingWaiver] = useState(false);
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
    data: BookedSkatingSessionHistoryResponse | null;
    loading: boolean;
  }>({
    isOpen: false,
    recordId: null,
    data: null,
    loading: false,
  });
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });
  const [attachWaiverModal, setAttachWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [attachedWaiver, setAttachedWaiver] = useState<CustomerWaiver | null>(
    null,
  );
  const [addTicketsModal, setAddTicketsModal] = useState<{
    isOpen: boolean;
    selectedTimeSlot: TimeSlot | null;
    ticketCount: string;
    notes: string;
  }>({
    isOpen: false,
    selectedTimeSlot: null,
    ticketCount: '',
    notes: '',
  });
  const [isAddingTickets, setIsAddingTickets] = useState(false);
  const [removeTicketsModal, setRemoveTicketsModal] = useState<{
    isOpen: boolean;
    selectedTimeSlot: TimeSlot | null;
    ticketCount: string;
    notes: string;
  }>({
    isOpen: false,
    selectedTimeSlot: null,
    ticketCount: '',
    notes: '',
  });
  const [isRemovingTickets, setIsRemovingTickets] = useState(false);
  const [changeSessionModal, setChangeSessionModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
    oldSessionId: number | null;
    selectedTimeSlot: TimeSlot | null;
    requestedByType: RequestedByType;
    notes: string;
  }>({
    isOpen: false,
    recordId: null,
    oldSessionId: null,
    selectedTimeSlot: null,
    requestedByType: 'Staff',
    notes: '',
  });
  const [isChangingSession, setIsChangingSession] = useState(false);
  const [isAttachingWaiver, setIsAttachingWaiver] = useState(false);
  const [recordPaperWaiverModal, setRecordPaperWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [isRecordingPaperWaiver, setIsRecordingPaperWaiver] = useState(false);
  const [isSessionFilterOpen, setIsSessionFilterOpen] = useState(false);
  const [selectedSessionInfo, setSelectedSessionInfo] = useState<{
    date: string;
    time: string;
  } | null>(null);
  const getTicketTypeLabel = (
    sessionData: SkatingSession,
  ): 'Adult' | 'Child' | null => {
    if (!sessionData) return null;

    const normalizedTicketType = sessionData.ticketType
      ? sessionData.ticketType.toLowerCase()
      : null;

    if (normalizedTicketType === 'adult') {
      return 'Adult';
    }

    if (normalizedTicketType === 'child') {
      return 'Child';
    }

    if (typeof sessionData.isAdult === 'boolean') {
      return sessionData.isAdult ? 'Adult' : 'Child';
    }

    if (typeof sessionData.isMinor === 'boolean') {
      return sessionData.isMinor ? 'Child' : 'Adult';
    }

    return null;
  };

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);

      const responseData = (await AdminService.getBookedSessions(
        pageNumber,
        pageSize,
        searchTerm,
        filterDate,
        searchSessionId,
      )) as unknown as PaginatedResponse<SkatingSession>;

      if (responseData) {
        setSessions(responseData.sessions || []);
        setTotalRecords(responseData.pagination?.totalRecords || 0);
        setTotalPages(responseData.pagination?.totalPages || 0);
        setHasNext(responseData.pagination?.hasNext || false);
        setHasPrevious(responseData.pagination?.hasPrevious || false);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm, filterDate, searchSessionId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = useCallback((searchTerm: string) => {
    // Only search if there are at least 3 characters or the search term is empty (to clear results)
    if (searchTerm.length >= 4 || searchTerm.length === 0) {
      setSearchTerm(searchTerm);
      setPageNumber(1); // Reset to first page when searching
    }
  }, []);

  const handleDateFilterChange = useCallback((date: string | null) => {
    setFilterDate(date);
    setPageNumber(1); // Reset to first page when changing date filter
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when changing page size
  };

  const handleRedeemTicket = (sessionId: number) => {
    setSelectedRecordId(sessionId);
    setShowConfirmModal(true);
  };

  const handleChangeSession = (recordId: number, oldSessionId: number) => {
    // Find the session to get the old session ID
    setChangeSessionModal({
      isOpen: true,
      recordId,
      oldSessionId,
      selectedTimeSlot: null,
      requestedByType: 'Staff',
      notes: '',
    });
  };

  const sessionToRedeem = sessions.find(s => s.id === selectedRecordId);
  const redeemNotToday =
    !!sessionToRedeem &&
    !HelperMethods.isDateToday(sessionToRedeem.sessionDate);

  const confirmRedeemTicket = async () => {
    if (!selectedRecordId) return;

    try {
      setRedeemingRecordId(selectedRecordId);
      // The API rejects other-day tickets unless staff confirmed the warning below
      await AdminService.redeemBookedSkatingSession(selectedRecordId, {
        allowDateMismatch: redeemNotToday,
      });

      await fetchSessions();
    } catch (error) {
      console.error('Error redeeming ticket:', error);
    } finally {
      setRedeemingRecordId(null);
    }
  };

  const openCancelModal = (recordId: number) => {
    setCancelModal({
      isOpen: true,
      recordId,
      requestedByType: 'Staff',
      reason: '',
    });
  };

  const closeCancelModal = () => {
    setCancelModal({
      isOpen: false,
      recordId: null,
      requestedByType: 'Staff',
      reason: '',
    });
  };

  const confirmCancelSession = async () => {
    if (!cancelModal.recordId) return;

    try {
      setDeletingRecordId(cancelModal.recordId);
      await AdminService.cancelSkatingSession(
        cancelModal.recordId,
        cancelModal.requestedByType,
        cancelModal.reason || undefined,
      );

      // Refresh the data
      await fetchSessions();
      closeCancelModal();
    } catch (error) {
      console.error('Error cancelling session:', error);
    } finally {
      setDeletingRecordId(null);
    }
  };

  const openDeleteModal = (recordId: number) => {
    setDeleteModal({
      isOpen: true,
      recordId,
      requestedByType: 'Staff',
      reason: '',
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      recordId: null,
      requestedByType: 'Staff',
      reason: '',
    });
  };

  const confirmDeleteSession = async () => {
    if (!deleteModal.recordId) return;

    try {
      setPermanentlyDeletingRecordId(deleteModal.recordId);
      await AdminService.permanentlyDeleteSkatingSession(
        deleteModal.recordId,
        deleteModal.requestedByType,
        deleteModal.reason || undefined,
      );

      // Refresh the data
      await fetchSessions();
      closeDeleteModal();
    } catch (error) {
      console.error('Error permanently deleting session:', error);
    } finally {
      setPermanentlyDeletingRecordId(null);
    }
  };

  const openRenameModal = (recordId: number) => {
    const session = sessions.find(s => s.id === recordId);
    setRenameModal({
      isOpen: true,
      recordId,
      currentName: session?.customerName || '',
      newCustomerName: session?.customerName || '',
      requestedByType: 'Staff',
      notes: '',
    });
  };

  const closeRenameModal = () => {
    setRenameModal({
      isOpen: false,
      recordId: null,
      currentName: '',
      newCustomerName: '',
      requestedByType: 'Staff',
      notes: '',
    });
  };

  const confirmRenameHolder = async () => {
    if (!renameModal.recordId || !renameModal.newCustomerName.trim()) return;

    try {
      setIsRenaming(true);
      await AdminService.renameSkatingSessionHolder(
        renameModal.recordId,
        renameModal.newCustomerName.trim(),
        renameModal.requestedByType,
        renameModal.notes || undefined,
      );

      // Refresh the data
      await fetchSessions();
      closeRenameModal();
    } catch (error) {
      console.error('Error renaming ticket holder:', error);
    } finally {
      setIsRenaming(false);
    }
  };

  const openRemoveWaiverModal = (recordId: number) => {
    setRemoveWaiverModal({ isOpen: true, recordId });
  };

  const closeRemoveWaiverModal = () => {
    setRemoveWaiverModal({ isOpen: false, recordId: null });
  };

  const confirmRemoveWaiver = async (
    requestedByType: RequestedByType,
    notes: string,
  ) => {
    if (!removeWaiverModal.recordId) return;

    try {
      setIsRemovingWaiver(true);
      await AdminService.removeAttachedWaiver(
        'skating_session',
        removeWaiverModal.recordId,
        requestedByType,
        notes || undefined,
      );
      await fetchSessions();
      closeRemoveWaiverModal();
    } catch (error) {
      console.error('Error removing waiver:', error);
    } finally {
      setIsRemovingWaiver(false);
    }
  };

  const openHistoryModal = async (recordId: number) => {
    setHistoryModal({ isOpen: true, recordId, data: null, loading: true });

    try {
      const data = (await AdminService.getBookedSkatingSessionHistory(
        recordId,
      )) as unknown as BookedSkatingSessionHistoryResponse;
      setHistoryModal({ isOpen: true, recordId, data, loading: false });
    } catch (error) {
      console.error('Error fetching session history:', error);
      setHistoryModal({ isOpen: true, recordId, data: null, loading: false });
    }
  };

  const closeHistoryModal = () => {
    setHistoryModal({
      isOpen: false,
      recordId: null,
      data: null,
      loading: false,
    });
  };

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  const openAttachWaiverModal = (recordId: number) => {
    setAttachWaiverModal({
      isOpen: true,
      recordId,
    });
    setAttachedWaiver(null);
  };

  const closeAttachWaiverModal = () => {
    setAttachWaiverModal({
      isOpen: false,
      recordId: null,
    });
    setAttachedWaiver(null);
  };

  const handleAttachWaiver = (waiver: CustomerWaiver) => {
    setAttachedWaiver(waiver);
  };

  const handleUnattachWaiver = () => {
    setAttachedWaiver(null);
  };

  const confirmAttachWaiver = async () => {
    if (
      !attachedWaiver ||
      !attachWaiverModal.recordId ||
      !attachedWaiver.pdfFilePath
    )
      return;

    try {
      setIsAttachingWaiver(true);
      await AdminService.attachCustomerWaiverToRecord(
        'skating_session',
        attachWaiverModal.recordId,
        attachedWaiver.id,
        attachedWaiver.pdfFilePath,
      );

      // Refresh the data
      await fetchSessions();
      closeAttachWaiverModal();
    } catch (error) {
      console.error('Error attaching waiver:', error);
    } finally {
      setIsAttachingWaiver(false);
    }
  };

  const openRecordPaperWaiverModal = (recordId: number) => {
    setRecordPaperWaiverModal({
      isOpen: true,
      recordId,
    });
  };

  const closeRecordPaperWaiverModal = () => {
    setRecordPaperWaiverModal({
      isOpen: false,
      recordId: null,
    });
  };

  const confirmRecordPaperWaiver = async () => {
    if (!recordPaperWaiverModal.recordId) return;

    try {
      setIsRecordingPaperWaiver(true);
      await AdminService.recordPaperWaiver(recordPaperWaiverModal.recordId);

      // Refresh the data
      await fetchSessions();
      closeRecordPaperWaiverModal();
    } catch (error) {
      console.error('Error recording paper waiver:', error);
    } finally {
      setIsRecordingPaperWaiver(false);
    }
  };

  const openAddTicketsModal = () => {
    setAddTicketsModal({
      isOpen: true,
      selectedTimeSlot: null,
      ticketCount: '',
      notes: '',
    });
  };

  const closeAddTicketsModal = () => {
    setAddTicketsModal({
      isOpen: false,
      selectedTimeSlot: null,
      ticketCount: '',
      notes: '',
    });
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    setAddTicketsModal(prev => ({
      ...prev,
      selectedTimeSlot: timeSlot,
    }));
  };

  const handleAddTickets = async () => {
    if (
      !addTicketsModal.selectedTimeSlot ||
      !addTicketsModal.ticketCount ||
      parseInt(addTicketsModal.ticketCount) <= 0
    ) {
      return;
    }

    try {
      setIsAddingTickets(true);
      await AdminService.addTicketsToSession({
        timeSlotId: addTicketsModal.selectedTimeSlot.id,
        totalTickets: parseInt(addTicketsModal.ticketCount),
        notes: addTicketsModal.notes,
      });

      // Refresh the data
      await fetchSessions();
      closeAddTicketsModal();
    } catch (error) {
      console.error('Error adding tickets:', error);
    } finally {
      setIsAddingTickets(false);
    }
  };

  const openRemoveTicketsModal = () => {
    setRemoveTicketsModal({
      isOpen: true,
      selectedTimeSlot: null,
      ticketCount: '',
      notes: '',
    });
  };

  const closeRemoveTicketsModal = () => {
    setRemoveTicketsModal({
      isOpen: false,
      selectedTimeSlot: null,
      ticketCount: '',
      notes: '',
    });
  };

  const handleTimeSlotSelectForRemove = (timeSlot: TimeSlot) => {
    setRemoveTicketsModal(prev => ({
      ...prev,
      selectedTimeSlot: timeSlot,
    }));
  };

  const handleRemoveTickets = async () => {
    if (
      !removeTicketsModal.selectedTimeSlot ||
      !removeTicketsModal.ticketCount ||
      parseInt(removeTicketsModal.ticketCount) <= 0
    ) {
      return;
    }

    try {
      setIsRemovingTickets(true);
      await AdminService.removeTicketsFromSession({
        timeSlotId: removeTicketsModal.selectedTimeSlot.id,
        totalTickets: parseInt(removeTicketsModal.ticketCount),
        notes: removeTicketsModal.notes,
      });

      await fetchSessions();
      closeRemoveTicketsModal();
    } catch (error) {
      console.error('Error removing tickets:', error);
    } finally {
      setIsRemovingTickets(false);
    }
  };

  const closeChangeSessionModal = () => {
    setChangeSessionModal({
      isOpen: false,
      recordId: null,
      oldSessionId: null,
      selectedTimeSlot: null,
      requestedByType: 'Staff',
      notes: '',
    });
  };

  const handleTimeSlotSelectForChange = (timeSlot: TimeSlot) => {
    setChangeSessionModal(prev => ({
      ...prev,
      selectedTimeSlot: timeSlot,
    }));
  };

  const handleDownloadExcel = () => {
    try {
      ExcelExport.exportSessionsToExcel(sessions);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleChangeSessionConfirm = async () => {
    if (
      !changeSessionModal.recordId ||
      !changeSessionModal.oldSessionId ||
      !changeSessionModal.selectedTimeSlot
    ) {
      return;
    }

    try {
      setIsChangingSession(true);
      await AdminService.changeSkatingSession({
        id: changeSessionModal.recordId,
        oldSessionId: changeSessionModal.oldSessionId,
        newSessionId: parseInt(changeSessionModal.selectedTimeSlot.id),
        requestedByType: changeSessionModal.requestedByType,
        notes: changeSessionModal.notes || undefined,
      });

      // Refresh the data
      await fetchSessions();
      closeChangeSessionModal();
    } catch (error) {
      console.error('Error changing session:', error);
    } finally {
      setIsChangingSession(false);
    }
  };

  const handleSessionFilterSelect = useCallback(
    (sessionId: string, sessionDate: string, sessionTime: string) => {
      setSearchSessionId(sessionId);
      setSelectedSessionInfo({ date: sessionDate, time: sessionTime });
      setIsSessionFilterOpen(false);
      setPageNumber(1); // Reset to first page when filtering by session
    },
    [],
  );

  const handleClearSessionFilter = useCallback(() => {
    setSearchSessionId(null);
    setSelectedSessionInfo(null);
    setPageNumber(1);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        {/* Title and Total Records Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Booked Skating Sessions
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage and view all booked skating sessions
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="text-sm text-gray-600">
              Total Records:{' '}
              <span className="font-semibold">{totalRecords}</span>
            </div>
            <button
              onClick={handleDownloadExcel}
              disabled={sessions.length === 0}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
                sessions.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Excel
            </button>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <button
            onClick={openAddTicketsModal}
            className="w-full sm:w-auto lg:flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-sm text-sm whitespace-nowrap"
          >
            Deduct Ticket(s)-TB Purchase
          </button>
          <button
            onClick={openRemoveTicketsModal}
            className="w-full sm:w-auto lg:flex-1 px-4 py-2.5 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2 transition-colors shadow-sm text-sm whitespace-nowrap"
          >
            Add Back Ticket(s)-TB Correction
          </button>

          <ViewTicketAvailability className="w-full sm:w-auto lg:flex-1" />
        </div>
      </div>

      {/* Search Bar with Filter by Session Button */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <SearchBar
              placeholder="Search by customer name, email, or barcode ID..."
              onSearch={handleSearch}
              debounceMs={1000}
              filterByDate="sessionDate"
              onDateFilterChange={handleDateFilterChange}
              defaultDateFilter="all"
            />
          </div>
          <button
            type="button"
            onClick={() => setIsSessionFilterOpen(true)}
            className="px-4 sm:px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 shadow-sm border border-gray-200"
            title="Filter by Session"
          >
            <MdFilterList size={20} />
            <span>By Session</span>
          </button>
        </div>

        {/* Selected Session Info */}
        {selectedSessionInfo && (
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3 sm:p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <MdFilterList className="text-purple-600 text-xl" />
              </div>
              <div>
                <div className="text-sm font-semibold text-purple-900">
                  Filtering by Session
                </div>
                <div className="text-sm text-purple-700">
                  {selectedSessionInfo.date} at{' '}
                  {HelperMethods.formatTime12Hour(selectedSessionInfo.time)}
                </div>
              </div>
            </div>
            <button
              onClick={handleClearSessionFilter}
              className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-full transition-colors"
              title="Clear session filter"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchSessions}
      />

      {/* Sessions Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Redeemed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No sessions found
                  </td>
                </tr>
              ) : (
                sessions.map(session => {
                  const ticketTypeLabel = getTicketTypeLabel(session);

                  return (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg text-gray-900">
                          <strong>
                            {HelperMethods.formatDate(session.sessionDate)} at{' '}
                            {HelperMethods.formatTime12Hour(session.timeSlot)}
                          </strong>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          Session #{session.skatingSessionId}
                        </div>
                        <div className="text-xs text-gray-700">
                          Barcode:{' '}
                          <span className="font-semibold text-blue-600">
                            {session.barcodeId}
                          </span>
                        </div>
                        {ticketTypeLabel && (
                          <div className="mt-2">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                ticketTypeLabel === 'Adult'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}
                            >
                              {ticketTypeLabel} Ticket
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {session.customerName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.customerEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.customerPhone}
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-center">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            session.redeemed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {session.redeemed ? 'Yes' : 'No'}
                        </span>
                        {/* {session.redeemed && session.redeemedBy && (
                          <div className="text-xs text-gray-500 mt-1">
                            by {session.redeemedBy}
                          </div>
                        )}
                        {session.redeemed && session.redeemedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            Redeemed on{' '}
                            {HelperMethods.formatDateTimeLocal(
                              session.redeemedAt,
                            )}
                          </div>
                        )} */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2 justify-between">
                          {session.waiverUrl ? (
                            <button
                              onClick={() =>
                                openPdfViewer(session.waiverUrl!, 'View Waiver')
                              }
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FaFileSignature className="w-5 h-5" />
                            </button>
                          ) : session.waiverType === 'Paper' ? (
                            <div className="flex items-start gap-1 text-xs text-green-700">
                              <FaFileSignature className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="font-semibold">
                                  Paper Waiver
                                </div>
                                {session.paperWaiverCompletedAt && (
                                  <div className="text-gray-500">
                                    {HelperMethods.formatDateTimeLocal(
                                      session.paperWaiverCompletedAt,
                                    )}
                                  </div>
                                )}
                                {session.paperWaiverRecordedByName && (
                                  <div className="text-gray-500">
                                    by {session.paperWaiverRecordedByName}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div className="text-gray-400">
                                <FaFileSignature className="w-5 h-5" />
                              </div>
                              <button
                                onClick={() =>
                                  openAttachWaiverModal(session.id)
                                }
                                className="text-green-600 hover:text-green-900"
                              >
                                Attach Waiver
                              </button>
                              <button
                                onClick={() =>
                                  openRecordPaperWaiverModal(session.id)
                                }
                                className="text-purple-600 hover:text-purple-900"
                              >
                                Record Paper Waiver
                              </button>
                            </div>
                          )}
                          {(session.waiverUrl ||
                            session.waiverId > 0 ||
                            session.waiverType === 'Paper') && (
                            <button
                              onClick={() => openRemoveWaiverModal(session.id)}
                              className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Remove Waiver
                            </button>
                          )}
                          {session.receiptUrl && (
                            <button
                              onClick={() =>
                                openPdfViewer(
                                  session.receiptUrl!,
                                  'View Receipt',
                                )
                              }
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="View Receipt"
                              aria-label="View Receipt"
                            >
                              <FaReceipt className="text-xl" />
                            </button>
                          )}
                          <div className="flex space-x-2">
                            <HistoryButton
                              hasHistory={session.hasHistory}
                              onClick={() => openHistoryModal(session.id)}
                            />
                            <button
                              onClick={() => handleRedeemTicket(session.id)}
                              disabled={
                                redeemingRecordId === session.id ||
                                session.redeemed
                              }
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                redeemingRecordId === session.id ||
                                session.redeemed
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                              }`}
                            >
                              {redeemingRecordId === session.id
                                ? 'Redeeming...'
                                : 'Redeem'}
                            </button>
                            <button
                              onClick={() =>
                                handleChangeSession(
                                  session.id,
                                  session.skatingSessionId,
                                )
                              }
                              disabled={session.redeemed}
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                session.redeemed
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                              }`}
                            >
                              Change
                            </button>
                            <button
                              onClick={() => openRenameModal(session.id)}
                              disabled={session.redeemed}
                              className={`px-3 py-1 text-xs rounded-md transition-colors inline-flex items-center gap-1 ${
                                session.redeemed
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                              }`}
                            >
                              <FaEdit className="w-3 h-3" />
                              Rename
                            </button>
                            <button
                              onClick={() => openCancelModal(session.id)}
                              disabled={
                                !canCancel ||
                                deletingRecordId === session.id ||
                                session.redeemed
                              }
                              title={
                                !canCancel
                                  ? 'You do not have permission to cancel tickets'
                                  : undefined
                              }
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                !canCancel ||
                                deletingRecordId === session.id ||
                                session.redeemed
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                              }`}
                            >
                              {deletingRecordId === session.id
                                ? 'Cancelling...'
                                : 'Cancel'}
                            </button>
                            <button
                              onClick={() => openDeleteModal(session.id)}
                              disabled={
                                !canDelete ||
                                permanentlyDeletingRecordId === session.id
                              }
                              title={
                                !canDelete
                                  ? 'You do not have permission to delete tickets'
                                  : undefined
                              }
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                !canDelete ||
                                permanentlyDeletingRecordId === session.id
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-red-800 text-white hover:bg-red-900 focus:ring-2 focus:ring-red-700 focus:ring-offset-2'
                              }`}
                            >
                              {permanentlyDeletingRecordId === session.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sessions Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {sessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No sessions found
          </div>
        ) : (
          sessions.map(session => {
            const ticketTypeLabel = getTicketTypeLabel(session);

            return (
              <div
                key={session.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
              >
                {/* Session Info */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Session #{session.skatingSessionId}
                    </h3>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.redeemed
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {session.redeemed ? 'Redeemed' : 'Not Redeemed'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {HelperMethods.formatDate(session.sessionDate)} at{' '}
                    {HelperMethods.formatTime12Hour(session.timeSlot)}
                  </div>
                  <div className="text-xs text-gray-700 mt-1">
                    Barcode:{' '}
                    <span className="font-semibold text-blue-600">
                      {session.barcodeId}
                    </span>
                  </div>
                  {ticketTypeLabel && (
                    <div className="mt-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                          ticketTypeLabel === 'Adult'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {ticketTypeLabel} Ticket
                      </span>
                    </div>
                  )}
                </div>

                {/* Customer Info */}
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Customer
                  </h4>
                  <div className="text-sm text-gray-600">
                    <div className="font-medium">{session.customerName}</div>
                    <div>{session.customerEmail}</div>
                    <div>{session.customerPhone}</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <HistoryButton
                    hasHistory={session.hasHistory}
                    onClick={() => openHistoryModal(session.id)}
                    size="md"
                  />
                  {session.waiverUrl ? (
                    <button
                      onClick={() =>
                        openPdfViewer(session.waiverUrl!, 'View Waiver')
                      }
                      className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                    >
                      <FaFileSignature className="w-5 h-5" />
                    </button>
                  ) : session.waiverType === 'Paper' ? (
                    <div className="inline-flex flex-col px-3 py-2 bg-green-50 text-green-700 text-xs rounded-md">
                      <span className="inline-flex items-center gap-1 font-semibold">
                        <FaFileSignature className="w-4 h-4" />
                        Paper Waiver
                      </span>
                      {session.paperWaiverCompletedAt && (
                        <span className="text-gray-500">
                          {HelperMethods.formatDateTimeLocal(
                            session.paperWaiverCompletedAt,
                          )}
                        </span>
                      )}
                      {session.paperWaiverRecordedByName && (
                        <span className="text-gray-500">
                          by {session.paperWaiverRecordedByName}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => openAttachWaiverModal(session.id)}
                        className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                      >
                        Attach Waiver
                      </button>
                      <button
                        onClick={() => openRecordPaperWaiverModal(session.id)}
                        className="inline-flex items-center px-3 py-2 bg-purple-100 text-purple-700 text-sm rounded-md hover:bg-purple-200 transition-colors"
                      >
                        Record Paper Waiver
                      </button>
                    </>
                  )}
                  {(session.waiverUrl ||
                    session.waiverId > 0 ||
                    session.waiverType === 'Paper') && (
                    <button
                      onClick={() => openRemoveWaiverModal(session.id)}
                      className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                    >
                      Remove Waiver
                    </button>
                  )}
                  {session.receiptUrl && (
                    <button
                      onClick={() =>
                        openPdfViewer(session.receiptUrl!, 'View Receipt')
                      }
                      className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                    >
                      <FaReceipt className="w-5 h-5" />
                    </button>
                  )}
                  {!session.redeemed && (
                    <button
                      onClick={() => handleRedeemTicket(session.id)}
                      disabled={redeemingRecordId === session.id}
                      className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        redeemingRecordId === session.id
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                      }`}
                    >
                      {redeemingRecordId === session.id
                        ? 'Redeeming...'
                        : 'Redeem'}
                    </button>
                  )}
                  <button
                    onClick={() =>
                      handleChangeSession(session.id, session.skatingSessionId)
                    }
                    disabled={session.redeemed}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      session.redeemed
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Change Session
                  </button>
                  <button
                    onClick={() => openRenameModal(session.id)}
                    disabled={session.redeemed}
                    className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${
                      session.redeemed
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    }`}
                  >
                    <FaEdit className="w-4 h-4" />
                    Rename
                  </button>
                  <button
                    onClick={() => openCancelModal(session.id)}
                    disabled={
                      !canCancel ||
                      deletingRecordId === session.id ||
                      session.redeemed
                    }
                    title={
                      !canCancel
                        ? 'You do not have permission to cancel tickets'
                        : undefined
                    }
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      !canCancel ||
                      deletingRecordId === session.id ||
                      session.redeemed
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {deletingRecordId === session.id
                      ? 'Cancelling...'
                      : 'Cancel'}
                  </button>
                  <button
                    onClick={() => openDeleteModal(session.id)}
                    disabled={
                      !canDelete || permanentlyDeletingRecordId === session.id
                    }
                    title={
                      !canDelete
                        ? 'You do not have permission to delete tickets'
                        : undefined
                    }
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      !canDelete || permanentlyDeletingRecordId === session.id
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-red-200 text-red-900 hover:bg-red-300'
                    }`}
                  >
                    {permanentlyDeletingRecordId === session.id
                      ? 'Deleting...'
                      : 'Delete'}
                  </button>
                </div>

                {/* Redeemed By Info */}
                {session.redeemed && session.redeemedBy && (
                  <div className="mt-3 text-xs text-gray-500">
                    Redeemed by: {session.redeemedBy}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmRedeemTicket}
        title="Redeem Ticket"
        message={
          redeemNotToday && sessionToRedeem
            ? `This ticket is for ${HelperMethods.formatDate(sessionToRedeem.sessionDate)}, not today. Confirm with the customer before redeeming. This action cannot be undone.`
            : 'Are you sure you want to redeem this ticket? This action cannot be undone.'
        }
        confirmText={redeemNotToday ? 'Redeem anyway' : 'Redeem'}
        cancelText="Cancel"
        type="warning"
      />

      {/* Record Paper Waiver Confirmation Modal */}
      <ConfirmationModal
        isOpen={recordPaperWaiverModal.isOpen}
        onClose={closeRecordPaperWaiverModal}
        onConfirm={confirmRecordPaperWaiver}
        title="Record Paper Waiver"
        message="Confirm that this customer completed a paper liability waiver at the Ticket Booth. This will be recorded under your staff account with today's date."
        confirmText={isRecordingPaperWaiver ? 'Recording...' : 'Confirm'}
        cancelText="Cancel"
        type="info"
      />

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />

      {/* Attach Waiver Modal */}
      {attachWaiverModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Attach Waiver
              </h2>
              <button
                onClick={closeAttachWaiverModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <SearchCustomerWaivers
                onAttach={handleAttachWaiver}
                onUnattach={handleUnattachWaiver}
                attachedWaiverId={`${attachedWaiver?.pdfFilePath}_${attachedWaiver?.barcodeId}`}
              />
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeAttachWaiverModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAttachWaiver}
                disabled={!attachedWaiver?.pdfFilePath}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  attachedWaiver?.pdfFilePath
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isAttachingWaiver ? 'Attaching...' : 'Confirm Attach'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tickets Modal */}
      {addTicketsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Remove Tickets from Session
              </h2>
              <button
                onClick={closeAddTicketsModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AddTicketsToSessionDateTime
                onSelectTimeSlot={handleTimeSlotSelect}
                selectedTimeSlot={addTicketsModal.selectedTimeSlot || undefined}
              />

              {/* Ticket Count and Notes Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="ticketCount"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Ticket Count <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    id="ticketCount"
                    min="1"
                    value={addTicketsModal.ticketCount}
                    onChange={e =>
                      setAddTicketsModal(prev => ({
                        ...prev,
                        ticketCount: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter number of tickets"
                  />
                </div>

                {/* <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Notes <span className="text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={addTicketsModal.notes}
                    onChange={e =>
                      setAddTicketsModal(prev => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Add any additional notes...Customer Name, Reciept No. etc."
                  />
                </div> */}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeAddTicketsModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTickets}
                disabled={
                  !addTicketsModal.selectedTimeSlot ||
                  !addTicketsModal.ticketCount ||
                  parseInt(addTicketsModal.ticketCount) <= 0 ||
                  isAddingTickets
                }
                className={`px-4 py-2 rounded-lg transition-colors ${
                  addTicketsModal.selectedTimeSlot &&
                  addTicketsModal.ticketCount &&
                  parseInt(addTicketsModal.ticketCount) > 0 &&
                  !isAddingTickets
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isAddingTickets
                  ? 'Removing Tickets...'
                  : 'Remove Tickets from Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Tickets Modal */}
      {removeTicketsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Back Tickets to Session
              </h2>
              <button
                onClick={closeRemoveTicketsModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AddTicketsToSessionDateTime
                onSelectTimeSlot={handleTimeSlotSelectForRemove}
                selectedTimeSlot={
                  removeTicketsModal.selectedTimeSlot || undefined
                }
              />

              {/* Ticket Count and Notes Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="ticketRemoveCount"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Ticket Count <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    id="ticketRemoveCount"
                    min="1"
                    value={removeTicketsModal.ticketCount}
                    onChange={e =>
                      setRemoveTicketsModal(prev => ({
                        ...prev,
                        ticketCount: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    placeholder="Enter number of tickets to remove"
                  />
                </div>

                {/* <div>
                  <label
                    htmlFor="removeNotes"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Notes <span className="text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    id="removeNotes"
                    rows={3}
                    value={removeTicketsModal.notes}
                    onChange={e =>
                      setRemoveTicketsModal(prev => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    placeholder="Add any additional notes...Customer Name, Receipt No. etc."
                  />
                </div> */}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeRemoveTicketsModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveTickets}
                disabled={
                  !removeTicketsModal.selectedTimeSlot ||
                  !removeTicketsModal.ticketCount ||
                  parseInt(removeTicketsModal.ticketCount) <= 0 ||
                  isRemovingTickets
                }
                className={`px-4 py-2 rounded-lg transition-colors ${
                  removeTicketsModal.selectedTimeSlot &&
                  removeTicketsModal.ticketCount &&
                  parseInt(removeTicketsModal.ticketCount) > 0 &&
                  !isRemovingTickets
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isRemovingTickets
                  ? 'Adding Back Tickets...'
                  : 'Add Back Tickets to Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Session Modal */}
      {changeSessionModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Change Session
              </h2>
              <button
                onClick={closeChangeSessionModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AddTicketsToSessionDateTime
                onSelectTimeSlot={handleTimeSlotSelectForChange}
                selectedTimeSlot={
                  changeSessionModal.selectedTimeSlot || undefined
                }
              />

              {/* Requested By and Notes Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requested By <span className="text-red-600">*</span>
                  </label>
                  <div className="flex gap-2">
                    {(['Customer', 'Staff'] as RequestedByType[]).map(
                      option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() =>
                            setChangeSessionModal(prev => ({
                              ...prev,
                              requestedByType: option,
                            }))
                          }
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            changeSessionModal.requestedByType === option
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {option}
                        </button>
                      ),
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="changeSessionNotes"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Notes <span className="text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    id="changeSessionNotes"
                    rows={2}
                    value={changeSessionModal.notes}
                    onChange={e =>
                      setChangeSessionModal(prev => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Any additional context about this change..."
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeChangeSessionModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeSessionConfirm}
                disabled={
                  !changeSessionModal.selectedTimeSlot || isChangingSession
                }
                className={`px-4 py-2 rounded-lg transition-colors ${
                  changeSessionModal.selectedTimeSlot && !isChangingSession
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isChangingSession ? 'Changing Session...' : 'Change Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Ticket Holder Modal */}
      {renameModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Rename Ticket Holder
              </h2>
              <button
                onClick={closeRenameModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MdClose size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Name
                </label>
                <div className="text-sm text-gray-600">
                  {renameModal.currentName}
                </div>
              </div>
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                If this ticket has a waiver, renaming the holder removes it. A
                new waiver matching the new name must be signed or attached.
              </p>
              <div>
                <label
                  htmlFor="newCustomerName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  New Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="newCustomerName"
                  value={renameModal.newCustomerName}
                  onChange={e =>
                    setRenameModal(prev => ({
                      ...prev,
                      newCustomerName: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="Enter new ticket holder name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requested By <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  {(['Customer', 'Staff'] as RequestedByType[]).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() =>
                        setRenameModal(prev => ({
                          ...prev,
                          requestedByType: option,
                        }))
                      }
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        renameModal.requestedByType === option
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor="renameNotes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Notes <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  id="renameNotes"
                  rows={2}
                  value={renameModal.notes}
                  onChange={e =>
                    setRenameModal(prev => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="Any additional context about this change..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeRenameModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRenameHolder}
                disabled={!renameModal.newCustomerName.trim() || isRenaming}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  renameModal.newCustomerName.trim() && !isRenaming
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isRenaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RemoveWaiverModal
        isOpen={removeWaiverModal.isOpen}
        isRemoving={isRemovingWaiver}
        onClose={closeRemoveWaiverModal}
        onConfirm={confirmRemoveWaiver}
      />

      {/* Cancel Ticket Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Cancel Ticket
              </h2>
              <button
                onClick={closeCancelModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MdClose size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to cancel this ticket? This action cannot
                be undone.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requested By <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  {(['Customer', 'Staff'] as RequestedByType[]).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() =>
                        setCancelModal(prev => ({
                          ...prev,
                          requestedByType: option,
                        }))
                      }
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        cancelModal.requestedByType === option
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor="cancelReason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  id="cancelReason"
                  rows={2}
                  value={cancelModal.reason}
                  onChange={e =>
                    setCancelModal(prev => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                  placeholder="Why is this ticket being cancelled?"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeCancelModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Keep Ticket
              </button>
              <button
                onClick={confirmCancelSession}
                disabled={deletingRecordId === cancelModal.recordId}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  deletingRecordId === cancelModal.recordId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {deletingRecordId === cancelModal.recordId
                  ? 'Cancelling...'
                  : 'Yes, Cancel Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Ticket Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Permanently Delete Ticket
              </h2>
              <button
                onClick={closeDeleteModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MdClose size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-red-700 font-medium">
                This permanently removes the ticket and cannot be undone.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requested By <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  {(['Customer', 'Staff'] as RequestedByType[]).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() =>
                        setDeleteModal(prev => ({
                          ...prev,
                          requestedByType: option,
                        }))
                      }
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        deleteModal.requestedByType === option
                          ? 'bg-red-800 text-white border-red-800'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor="deleteReason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  id="deleteReason"
                  rows={2}
                  value={deleteModal.reason}
                  onChange={e =>
                    setDeleteModal(prev => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-700 focus:border-red-700 transition-colors"
                  placeholder="Why is this ticket being permanently deleted?"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Keep Ticket
              </button>
              <button
                onClick={confirmDeleteSession}
                disabled={permanentlyDeletingRecordId === deleteModal.recordId}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  permanentlyDeletingRecordId === deleteModal.recordId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-800 text-white hover:bg-red-900'
                }`}
              >
                {permanentlyDeletingRecordId === deleteModal.recordId
                  ? 'Deleting...'
                  : 'Yes, Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket History Modal */}
      {historyModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Ticket Change History
              </h2>
              <button
                onClick={closeHistoryModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MdClose size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {historyModal.loading ? (
                <div className="text-center text-gray-500 py-8">
                  Loading history...
                </div>
              ) : !historyModal.data ? (
                <div className="text-center text-gray-500 py-8">
                  Unable to load history for this ticket.
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Ticket Details */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Current Ticket Details
                    </div>
                    <div className="text-sm text-gray-900 font-medium">
                      {HelperMethods.formatDate(
                        historyModal.data.originalSessionDate,
                      )}{' '}
                      at{' '}
                      {HelperMethods.formatTime12Hour(
                        historyModal.data.originalTimeSlot,
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Ticket holder: {historyModal.data.originalCustomerName}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Purchased{' '}
                      {HelperMethods.formatDateTimeLocal(
                        historyModal.data.originalPurchasedAt,
                      )}
                    </div>
                  </div>

                  {/* Timeline */}
                  {historyModal.data.history.length === 0 ? (
                    <div className="text-center text-gray-500 py-4 text-sm">
                      No changes have been made to this ticket.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {historyModal.data.history.map(entry => (
                        <div
                          key={entry.id}
                          className={`rounded-lg p-4 border ${
                            entry.changeType === 'Cancelled'
                              ? 'bg-red-50 border-red-200'
                              : entry.changeType === 'NameChanged'
                                ? 'bg-indigo-50 border-indigo-200'
                                : entry.changeType === 'WaiverRemoved'
                                  ? 'bg-amber-50 border-amber-200'
                                  : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`text-xs font-semibold uppercase tracking-wider ${
                                entry.changeType === 'Cancelled'
                                  ? 'text-red-700'
                                  : entry.changeType === 'NameChanged'
                                    ? 'text-indigo-700'
                                    : entry.changeType === 'WaiverRemoved'
                                      ? 'text-amber-700'
                                      : 'text-blue-700'
                              }`}
                            >
                              {entry.changeType === 'Rescheduled' &&
                                'Rescheduled'}
                              {entry.changeType === 'NameChanged' &&
                                'Name Changed'}
                              {entry.changeType === 'Cancelled' && 'Cancelled'}
                              {entry.changeType === 'WaiverRemoved' &&
                                'Waiver Removed'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {HelperMethods.formatDateTimeLocal(
                                entry.changedAt,
                              )}
                            </span>
                          </div>

                          {entry.changeType === 'Rescheduled' && (
                            <div className="text-sm text-gray-900">
                              {entry.previousSessionDate &&
                                entry.previousTimeSlot && (
                                  <>
                                    {HelperMethods.formatDate(
                                      entry.previousSessionDate,
                                    )}{' '}
                                    at{' '}
                                    {HelperMethods.formatTime12Hour(
                                      entry.previousTimeSlot,
                                    )}
                                  </>
                                )}
                              {' → '}
                              {entry.newSessionDate && entry.newTimeSlot && (
                                <>
                                  {HelperMethods.formatDate(
                                    entry.newSessionDate,
                                  )}{' '}
                                  at{' '}
                                  {HelperMethods.formatTime12Hour(
                                    entry.newTimeSlot,
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {entry.changeType === 'NameChanged' && (
                            <div className="text-sm text-gray-900">
                              {entry.previousCustomerName} {' → '}
                              {entry.newCustomerName}
                            </div>
                          )}

                          {entry.changeType === 'WaiverRemoved' && (
                            <div className="text-sm text-gray-900">
                              <div>
                                {entry.previousWaiverType === 'Paper'
                                  ? 'Paper waiver removed'
                                  : 'Digital waiver removed'}
                              </div>
                              {entry.previousWaiverUrl && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openPdfViewer(
                                      entry.previousWaiverUrl!,
                                      'Removed Waiver',
                                    )
                                  }
                                  className="mt-1 text-sm text-blue-600 hover:text-blue-800"
                                >
                                  View previous waiver
                                </button>
                              )}
                            </div>
                          )}

                          {entry.changeType === 'Cancelled' &&
                            entry.cancelledReason && (
                              <div className="text-sm text-gray-900">
                                Reason: {entry.cancelledReason}
                              </div>
                            )}

                          <div className="text-xs text-gray-600 mt-2 space-y-0.5">
                            <div>
                              Requested by: {entry.requestedByType}
                              {entry.requestedByNotes &&
                                ` — ${entry.requestedByNotes}`}
                            </div>
                            <div>
                              Processed by:{' '}
                              {entry.processedByName || 'Staff member'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeHistoryModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Filter Modal */}
      {isSessionFilterOpen && (
        <ViewTicketAvailability
          isOpen={isSessionFilterOpen}
          onClose={() => setIsSessionFilterOpen(false)}
          onSessionSelect={handleSessionFilterSelect}
        />
      )}
    </div>
  );
};

export default BookedSkatingSessions;
